import { timezoneUtils } from "@/lib/timezone";
import { UserNotification } from "../types";

// Sort functions
export const sortUserNotificationsByDate = (
  notifications: UserNotification[],
  order: "asc" | "desc" = "desc"
): UserNotification[] => {
  return [...notifications].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return order === "desc" ? dateB - dateA : dateA - dateB;
  });
};

// Legacy function for backward compatibility
export const sortNotificationsByDate = sortUserNotificationsByDate;

export const sortUserNotificationsByPriority = (
  notifications: UserNotification[],
  order: "asc" | "desc" = "desc"
): UserNotification[] => {
  const priorityOrder = ["URGENT", "HIGH", "NORMAL", "LOW"];

  return [...notifications].sort((a, b) => {
    const priorityA = priorityOrder.indexOf(a.priority);
    const priorityB = priorityOrder.indexOf(b.priority);

    if (order === "desc") {
      return priorityA - priorityB;
    }
    return priorityB - priorityA;
  });
};

// Legacy function for backward compatibility
export const sortNotificationsByPriority = sortUserNotificationsByPriority;

// Transform functions
export const getUserNotificationDisplayName = (notification: UserNotification): string => {
  return notification.title;
};

// Legacy function for backward compatibility
export const getNotificationDisplayName = getUserNotificationDisplayName;

export const getNotificationCategoryColor = (category: string): string => {
  switch (category.toUpperCase()) {
    case "APPOINTMENT":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "LOYALTY":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case "PROMOTION":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "SYSTEM":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

export const getNotificationPriorityColor = (priority: string): string => {
  switch (priority.toUpperCase()) {
    case "URGENT":
      return "bg-red-500";
    case "HIGH":
      return "bg-orange-500";
    case "NORMAL":
      return "bg-blue-500";
    case "LOW":
      return "bg-gray-500";
    default:
      return "bg-gray-500";
  }
};

export const getNotificationCategoryIcon = (category: string) => {
  switch (category.toUpperCase()) {
    case "APPOINTMENT":
      return "Calendar";
    case "LOYALTY":
      return "Star";
    case "PROMOTION":
      return "Gift";
    case "SYSTEM":
      return "Settings";
    default:
      return "Bell";
  }
};

/**
 * Gets an appropriate icon for a notification reference type
 */
export const getReferenceTypeIcon = (referenceType: string) => {
  switch (referenceType.toUpperCase()) {
    case "APPOINTMENT":
      return "Calendar";
    case "WAITING_LIST":
      return "Clock";
    case "WORKER_SCHEDULE":
    case "WORKER":
      return "Users";
    case "CUSTOMER":
      return "User";
    case "SERVICE":
      return "Scissors";
    case "SALON_SETTINGS":
      return "Building";
    case "MESSAGE":
      return "MessageSquare";
    case "AUDIT_LOG":
      return "FileText";
    case "SYSTEM":
      return "Settings";
    default:
      return "Circle";
  }
};

/**
 * Gets a color class for a notification reference type
 */
export const getReferenceTypeColor = (referenceType: string): string => {
  switch (referenceType.toUpperCase()) {
    case "APPOINTMENT":
      return "text-blue-600 dark:text-blue-400";
    case "WAITING_LIST":
      return "text-orange-600 dark:text-orange-400";
    case "WORKER_SCHEDULE":
    case "WORKER":
      return "text-purple-600 dark:text-purple-400";
    case "CUSTOMER":
      return "text-green-600 dark:text-green-400";
    case "SERVICE":
      return "text-pink-600 dark:text-pink-400";
    case "SALON_SETTINGS":
      return "text-indigo-600 dark:text-indigo-400";
    case "MESSAGE":
      return "text-cyan-600 dark:text-cyan-400";
    case "AUDIT_LOG":
      return "text-gray-600 dark:text-gray-400";
    case "SYSTEM":
      return "text-red-600 dark:text-red-400";
    default:
      return "text-muted-foreground";
  }
};

/**
 * Formats reference information for display
 */
export const formatReferenceInfo = (
  referenceType: string | null | undefined,
  referenceId: string | null | undefined
): string | null => {
  if (!referenceType || !referenceId) {
    return null;
  }

  const displayName = getReferenceTypeDisplayName(referenceType);
  return `${displayName} • ${referenceId}`;
};

/**
 * Gets a human-readable display name for a reference type
 */
const getReferenceTypeDisplayName = (referenceType: string): string => {
  switch (referenceType.toUpperCase()) {
    case "APPOINTMENT":
      return "Appointment";
    case "WAITING_LIST":
      return "Waiting List";
    case "WORKER_SCHEDULE":
      return "Worker Schedule";
    case "CUSTOMER":
      return "Customer";
    case "SERVICE":
      return "Service";
    case "WORKER":
      return "Staff Member";
    case "SALON_SETTINGS":
      return "Salon Settings";
    case "SYSTEM":
      return "System";
    case "MESSAGE":
      return "Message";
    case "AUDIT_LOG":
      return "Audit Log";
    default:
      return referenceType;
  }
};

export const formatNotificationTime = (createdAt: string): string => {
  const dateTime = timezoneUtils.fromISO(createdAt);

  // Use Luxon's toRelative method for consistent relative formatting
  return dateTime.toRelative() || "Unknown time";
};

const SMS_SENT_STATUSES = ["SENT", "DELIVERED"];

export const hasSentSmsDelivery = (notification: UserNotification): boolean =>
  notification.deliveryEvents?.some(
    (event) =>
      event.channel.toLowerCase() === "sms" &&
      SMS_SENT_STATUSES.includes(event.status.toUpperCase())
  ) ?? false;
